import { Language } from '@configs/language';
import DueDateSpan from '@constants/dues';
import { DecimalDigits } from '@constants/enums';
import { StateSpan } from '@constants/states';
import {
  NewPaymentMethods,
  TransactionTypes,
} from '@efex-components/payables/PaymentModal/types';
import { PayableTypes } from '@efex-services/client/types';
import { IEnterprisePayment } from '@efex-services/payments/types';
import { Avatar, Button } from '@efexteam/components';
import theme from '@efexteam/mui-theme';
import { Actions, Can, Subjects } from '@efexteam/permission-manager';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import {
  Card,
  CardContent,
  Collapse,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  CURRENCY_IDS,
  convertDateToHumanReadable,
  formatCurrency,
} from '@utils/formater';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { CardHeader } from 'reactstrap';
import PaymentsTableStyle from './PaymentsTable.style';
import { IPaymentRowProps } from './types';

const PaymentRow = ({
  payment,
  expanded,
  isReadOnly,
  payableType,
  handleExpandClick,
  isValidExecutionDate,
  getStatusName,
  renderCompleteRateValue,
  renderRateValue,
  getScheduleDate,
  showInvoice,
  handlePayout,
  handleRejectPayable,
  setPayForward,
  navigateToTransferDetails,
}: IPaymentRowProps) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const renderPaymentActions = (paymentData: IEnterprisePayment) => {
    if (
      paymentData.status.name === PayableTypes.PENDING_TO_APPROVE ||
      paymentData.status.name === PayableTypes.PENDING_TO_APPROVE_BY_USER
    ) {
      return (
        <Can action={Actions.CREATE} subject={Subjects.PAYMENTS}>
          <TableCell className={PaymentsTableStyle.tableCell()}>
            <IconButton onClick={() => navigateToTransferDetails(paymentData)}>
              <KeyboardArrowRightOutlinedIcon />
            </IconButton>
          </TableCell>
        </Can>
      );
    }

    return (
      <TableCell className={PaymentsTableStyle.tableCell()}>
        <Can action={Actions.UPDATE} subject={Subjects.PAYMENTS}>
          {paymentData.status.name ===
          PayableTypes.PENDING_TO_PROCESS_SCHEDULED_PAYMENT ? (
            <IconButton size="small" onClick={() => setPayForward(paymentData)}>
              <PaymentsOutlinedIcon />
            </IconButton>
          ) : null}
        </Can>
        <Can action={Actions.READ} subject={Subjects.PAYMENTS}>
          <IconButton
            size="small"
            onClick={() => navigateToTransferDetails(paymentData)}
          >
            <KeyboardArrowRightOutlinedIcon />
          </IconButton>
        </Can>
        <Can action={Actions.READ} subject={Subjects.PAYMENTS}>
          {paymentData.status.name === PayableTypes.NOT_PROCESSED ? (
            <p>-</p>
          ) : (
            <IconButton
              size="small"
              onClick={() =>
                showInvoice(
                  paymentData.uuid,
                  paymentData.transactionTypeId === TransactionTypes.swift ||
                    paymentData.paymentMethodId === NewPaymentMethods.forward
                    ? 'payment'
                    : 'payable'
                )
              }
            >
              <DescriptionOutlinedIcon />
            </IconButton>
          )}
        </Can>
        {!isReadOnly && ( // TODO: remove this as part of pay payment request deprecation
          <>
            {paymentData.status.name === PayableTypes.PENDING_TO_PROCESS &&
              payableType === PayableTypes.PENDING && (
                <Button
                  appearance="primary"
                  variant="outlined"
                  size="small"
                  className="!mr-2"
                  onClick={() => handlePayout(paymentData)}
                >
                  {t('pay')}
                </Button>
              )}
            {paymentData.status.name === PayableTypes.PENDING_TO_PROCESS &&
              payableType === PayableTypes.PENDING && (
                <Button
                  appearance="primary"
                  variant="outlined"
                  size="small"
                  onClick={() => handleRejectPayable(paymentData)}
                >
                  {t('reject')}
                </Button>
              )}
          </>
        )}
      </TableCell>
    );
  };

  return (
    <>
      <TableRow key={`payment-${payment.uuid}`}>
        <TableCell className={PaymentsTableStyle.tableCell(!isMobile)}>
          <div className={PaymentsTableStyle.clientNameCell}>
            <section className="client-name-section">
              <Avatar
                username={
                  payment.contact?.enterprise?.name.length
                    ? payment.contact?.enterprise?.name
                    : `${payment.contact?.naturalPerson?.firstName ?? ''} ${
                        payment.contact?.naturalPerson?.lastName ?? ''
                      }`
                }
                size="sm"
              />
              <p className="client-name-text">
                {payment.contact?.enterprise?.name.length
                  ? payment.contact?.enterprise?.name
                  : `${payment.contact?.naturalPerson?.firstName ?? ''} ${
                      payment.contact?.naturalPerson?.lastName ?? ''
                    }`}
              </p>
            </section>
            {payment.partialPayments?.length ? (
              <IconButton onClick={() => handleExpandClick(payment.id)}>
                <ExpandMoreIcon
                  className={`transition-all ${
                    expanded === payment.id ? 'rotate-180' : 'rotate-0'
                  }`}
                />
              </IconButton>
            ) : null}
            {payment.paymentMethodId === NewPaymentMethods.forward &&
            isValidExecutionDate((payment as any).executionDate) ? (
              <Tooltip
                title={t('forwardWarning', {
                  date: convertDateToHumanReadable(
                    (payment as any).executionDate.split(' ')[0],
                    language.toUpperCase() as Language,
                    DateTime.DATE_MED
                  ),
                })}
              >
                <IconButton>
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            ) : null}
          </div>
        </TableCell>
        <TableCell className={PaymentsTableStyle.tableCell()}>
          <span className="sr-only">{t('paymentStatus')}:</span>
          <StateSpan status={getStatusName(payment)} />
        </TableCell>
        <TableCell className={PaymentsTableStyle.tableCell()}>
          <span title={renderCompleteRateValue(payment.rate)}>
            {renderRateValue(payment.rate)}
          </span>
        </TableCell>
        <TableCell className={PaymentsTableStyle.tableCell()}>
          {convertDateToHumanReadable(
            getScheduleDate(payment),
            language.toUpperCase() as Language,
            DateTime.DATE_MED
          )}
        </TableCell>
        <TableCell className={PaymentsTableStyle.tableCell()}>
          {formatCurrency(
            payment.amount,
            DecimalDigits.DEFAULT_DIGITS,
            DecimalDigits.DEFAULT_MIN_DIGITS,
            CURRENCY_IDS[payment.currencyId]
          )}{' '}
          {CURRENCY_IDS[payment.currencyId]}
        </TableCell>
        <TableCell className={PaymentsTableStyle.tableCell()}>
          {formatCurrency(
            payment.desAmount,
            DecimalDigits.DEFAULT_DIGITS,
            DecimalDigits.DEFAULT_MIN_DIGITS,
            CURRENCY_IDS[payment.desCurrencyId]
          )}{' '}
          {CURRENCY_IDS[payment.desCurrencyId]}
        </TableCell>
        <TableCell className={PaymentsTableStyle.tableCell()}>
          <DueDateSpan
            state={payment.status.name}
            value={convertDateToHumanReadable(
              isValidExecutionDate((payment as any).executionDate) &&
                payment.paymentMethodId === NewPaymentMethods.forward
                ? new Date((payment as any).executionDate).toISOString()
                : payment.dueDate,
              language.toUpperCase() as Language,
              DateTime.DATE_MED
            )}
          />
        </TableCell>
        {renderPaymentActions(payment)}
      </TableRow>
      {payment.partialPayments?.length && expanded === payment.id ? (
        <TableRow>
          <TableCell colSpan={8}>
            <Collapse in={!!expanded} timeout="auto" unmountOnExit>
              <div className="w-full grid gap-3 grid-cols-4">
                {payment.partialPayments.map((partial) => (
                  <Card key={partial.id}>
                    <CardHeader className="pt-3 px-3">
                      {t('parcialPaymentTitle')}
                    </CardHeader>
                    <CardContent className="grid gap-3 grid-cols-2">
                      {partial.amount ? (
                        <p>
                          <span className="font-bold">
                            {t('parcialPaymentAmount')}:{' '}
                          </span>
                          <br />
                          {formatCurrency(partial.amount)}{' '}
                          {CURRENCY_IDS[payment.currencyId]}
                        </p>
                      ) : null}
                      {partial.percentage ? (
                        <p>
                          <span className="font-bold">
                            {t('parcialPaymentPercentage')}:{' '}
                          </span>
                          <br />
                          {partial.percentage} %
                        </p>
                      ) : null}
                      {partial.convertedAmount ? (
                        <p>
                          <span className="font-bold">{t('desAmount')}: </span>
                          <br />
                          {formatCurrency(partial.convertedAmount)}{' '}
                          {CURRENCY_IDS[payment.desCurrencyId]}
                        </p>
                      ) : null}
                      {partial.createdAt ? (
                        <p>
                          <span className="font-bold">
                            {t('creationDate')}:{' '}
                          </span>
                          <br />
                          {convertDateToHumanReadable(
                            partial.createdAt,
                            language,
                            DateTime.DATE_MED
                          )}
                        </p>
                      ) : null}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </Collapse>
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
};

export default PaymentRow;
